import {setTitle as setDocumentTitle} from '@github-ui/document-metadata'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type React from 'react'
import {useEffect, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, useFragment} from 'react-relay'
import {usePreloadedQuery} from 'react-relay/hooks'

import {VALUES} from '../../constants'
import type {Header$data, Header$key} from './__generated__/Header.graphql'
import type {HeaderIssueTitle$key} from './__generated__/HeaderIssueTitle.graphql'
import type {HeaderQuery} from './__generated__/HeaderQuery.graphql'
import {HeaderEditor} from './HeaderEditor'
import {HeaderViewer} from './HeaderViewer'
import type {HeaderMenuSharedProps} from './HeaderMenu'
import {Flash, IconButton} from '@primer/react'
import {useIssueViewerIssueEditContext} from '../../contexts'
import {HeaderMetadata} from './HeaderMetadata'
import {ObservableBox, useStickyHeader} from '@github-ui/use-sticky-header'
import {ContentWrapper} from '../ContentWrapper'
import {XIcon} from '@primer/octicons-react'
import type {OptionConfig} from '../OptionConfig'

export const HeaderGraphqlQuery = graphql`
  query HeaderQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...Header
        issueType {
          ...IssueTypesDialogIssueType
        }
      }
      issueTypes(first: 10) {
        ...IssueTypesDialogIssueTypes
      }
    }
  }
`

const headerIssueTitleFragment = graphql`
  fragment HeaderIssueTitle on Issue {
    title
    titleHTML
    number
    repository {
      nameWithOwner
    }
  }
`

type HeaderBaseProps = {
  optionConfig: OptionConfig
  issueId: string
} & HeaderMenuSharedProps

export type HeaderFetchedProps = HeaderBaseProps & {
  issuesQueryRef: PreloadedQuery<HeaderQuery>
}

type HeaderProps = HeaderBaseProps & {
  issue: Header$key | null
}

type HeaderInternalProps = Omit<HeaderProps, 'issue'> & {
  data: Header$data
}

export function HeaderFetched({issuesQueryRef, ...rest}: HeaderFetchedProps) {
  const preloadedData = usePreloadedQuery<HeaderQuery>(HeaderGraphqlQuery, issuesQueryRef)
  return (
    <Header
      issue={preloadedData.repository && preloadedData.repository.issue ? preloadedData.repository.issue : null}
      {...rest}
    />
  )
}

export function Header({issue, ...rest}: HeaderProps) {
  const data = useFragment(
    graphql`
      fragment Header on Issue {
        author {
          login
        }
        viewerCanUpdateNext
        isTransferInProgress
        createdAt
        comments {
          totalCount
        }
        repository {
          id
          name
          isPrivate
          owner {
            __typename
            avatarUrl(size: 64)
            login
          }
        }
        url
        ...HeaderState_issue
        ...HeaderIssueTitle
        ...HeaderMenu
        tasklistBlocksCompletion {
          ...TaskListStatus
        }
        issueType {
          id
          name
        }
      }
    `,
    issue,
  )
  return data ? <HeaderInternal data={data} {...rest} /> : null
}

function HeaderInternal({optionConfig, data, issueId, ...props}: HeaderInternalProps) {
  const {
    title: issueTitle,
    titleHTML: issueTitleHtml,
    number,
    repository: {nameWithOwner},
  } = useFragment(headerIssueTitleFragment, data as HeaderIssueTitle$key)

  const [title, setTitle] = useState<string>(issueTitle)
  const [titleHTML, setTitleHTML] = useState<string | undefined>(issueTitleHtml)

  const shouldUpdateDocumentTitle = optionConfig.selectedProjectId === undefined
  const titleRef = useRef<HTMLInputElement>(null)
  const {isIssueEditActive, endIssueEdit} = useIssueViewerIssueEditContext()
  const [isIssueTitleEditActive, setIsIssueTitleEditActive] = useState(isIssueEditActive)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isIssueEditActive) {
      setIsIssueTitleEditActive(false)
    }
  }, [isIssueEditActive])

  useEffect(() => {
    if (issueTitle && shouldUpdateDocumentTitle) {
      setDocumentTitle(`${title} · Issue #${number} · ${nameWithOwner}`)
    }
  }, [shouldUpdateDocumentTitle, issueTitle, nameWithOwner, number, title])

  useEffect(() => {
    if (isIssueTitleEditActive) return
    setTitle(issueTitle)
  }, [isIssueTitleEditActive, issueTitle, setTitle])

  useEffect(() => setTitleHTML(issueTitleHtml), [issueTitleHtml])

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = title
    const newValue = e.target.value
    if (newValue !== null && newValue !== oldValue) {
      setTitle(newValue)
    }
  }

  // Focus on title when user clicks on edit
  useEffect(() => {
    if (isIssueTitleEditActive && titleRef.current) {
      titleRef.current.focus()
    }
  }, [isIssueTitleEditActive])

  const {isSticky, stickyStyles, observe, unobserve} = useStickyHeader()
  const [showTransferWarning, setShowTransferWarning] = useState(data.isTransferInProgress)

  if (!data) {
    return null
  }

  const headerMetadata = {
    stateData: data,
    repository: {
      isPrivate: data.repository.isPrivate,
      ownerAvatarUrl: data.repository.owner.avatarUrl,
      ownerType: data.repository.owner.__typename,
    },
    number,
    titleHTML: titleHTML as SafeHTMLString,
    url: data.url,
    repoId: data.repository.id,
    repoName: data.repository.name,
    repoOwner: data.repository.owner.login,
    commentsCount: data.comments.totalCount,
    typeData: data.issueType,
    isSticky,
  }

  const headerData = {
    issue: data,
    menuData: data,
    createdAt: data.createdAt,
    login: data.author ? data.author.login : VALUES.ghostUserLogin,
    markdownEditorConfig: props.markdownEditorConfig,
    setIsIssueTitleEditActive,
    editInProgress: isIssueEditActive,
    viewerCanUpdate: data.viewerCanUpdateNext || false,
    ...headerMetadata,
    ...props,
  }

  return (
    <>
      <ContentWrapper sx={optionConfig.innerSx}>
        {showTransferWarning && (
          <Flash
            aria-live="polite"
            sx={{mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
          >
            Issue transfer in progress
            <IconButton
              variant="invisible"
              aria-label="Dismiss issue is in transfer notification"
              icon={XIcon}
              onClick={() => setShowTransferWarning(false)}
            />
          </Flash>
        )}
        {isIssueTitleEditActive || isSubmitting ? (
          <HeaderEditor
            issueId={issueId}
            title={title}
            titleRef={titleRef}
            onTitleChange={onTitleChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            cancelIssueTitleEdit={() => {
              setIsSubmitting(false)
              setIsIssueTitleEditActive(false)
              endIssueEdit()
            }}
            isDirty={issueTitle !== title}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        ) : (
          <HeaderViewer {...headerData} />
        )}
      </ContentWrapper>
      <ObservableBox
        sx={{top: '-1px', height: '1px', visibility: 'hidden'}}
        onObserve={observe}
        onUnobserve={unobserve}
      />
      <HeaderMetadata
        {...headerMetadata}
        showRepositoryPill={optionConfig.showRepositoryPill}
        headerMenuProps={headerData}
        headerTitleProps={headerMetadata}
        isSticky={isSticky}
        stickyStyles={stickyStyles}
        taskListStatusData={data.tasklistBlocksCompletion ?? undefined}
        innerSx={optionConfig.innerSx}
      />
    </>
  )
}

try{ HeaderFetched.displayName ||= 'HeaderFetched' } catch {}
try{ Header.displayName ||= 'Header' } catch {}
try{ HeaderInternal.displayName ||= 'HeaderInternal' } catch {}